import { lazy, Suspense } from "react";
import RequireRole, { ANY_ROLE } from "./components/requireRole.jsx";
import AboutUs from "./routes/aboutUs";
import Catalogue from "./routes/catalogue";
import CategoryPage from "./routes/categoryPage.jsx";
import ContactPage from "./routes/contactPage";
import Faq from "./routes/faq";
import Financing from "./routes/financing";
import HomePage from "./routes/homePage";
import JobDescription from "./routes/jobDescription";
import JobOffers from "./routes/jobOffers";
import Machine from "./routes/machine";
import ManufacturerPage from "./routes/manufacturerPage.jsx";
import News from "./routes/news.jsx";
import NotFound from "./routes/notFound.jsx";
import NewsCatalogue from "./routes/newsCatalogue.jsx";
import Quotation from "./routes/quotation";
import TermsAndConditions from "./routes/termsAndConditions";
import { categoryUrlSlug, machineCategoryUrlSlug, slugify } from "./slug.js";

/**
 * Admin screens are code-split. They sit behind a login, nobody reaches them from a search
 * result, and between them they pull in the heaviest dependencies on the site
 * (@react-pdf/renderer, react-select, the markdown editors) — statically imported they rode
 * along in the one bundle every public visitor downloads.
 *
 * Public routes stay statically imported on purpose: entry-server.jsx renders them with
 * renderToString(), which cannot wait for a chunk and would emit the Suspense fallback instead
 * of the page.
 */
const Admin = lazy(() => import("./routes/admin.jsx"));
const CategoriesAdmin = lazy(() => import("./routes/categoriesAdmin.jsx"));
const CreateCustomer = lazy(() => import("./routes/createCustomer.jsx"));
const CreateMachine = lazy(() => import("./routes/createMachine"));
const CreateNews = lazy(() => import("./routes/createNews"));
const CreateQuotation = lazy(() => import("./routes/createQuotation"));
const CustomerQuotations = lazy(() => import("./routes/customerQuotations.jsx"));
const Customers = lazy(() => import("./routes/customers.jsx"));
const FaqAdmin = lazy(() => import("./routes/faqAdmin.jsx"));
const JobsAdmin = lazy(() => import("./routes/jobsAdmin.jsx"));
const QuotationManagement = lazy(() => import("./routes/quotationManagement"));
const SiteContentAdmin = lazy(() => import("./routes/siteContentAdmin.jsx"));
const TeamAdmin = lazy(() => import("./routes/teamAdmin.jsx"));
const TermsAdmin = lazy(() => import("./routes/termsAdmin.jsx"));
const UnpublishedMachineManagement = lazy(() => import("./routes/unpublishedMachineManagement"));
const UsersAdmin = lazy(() => import("./routes/usersAdmin.jsx"));

const RouteFallback = () => (
  <div className="flex items-center justify-center py-24" data-testid="route-loading">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

/** Route element for a lazily loaded screen, with its own boundary so only it shows a spinner. */
const lazyElement = (Component) => (
  <Suspense fallback={<RouteFallback />}>
    <Component />
  </Suspense>
);

/**
 * Route element for an admin screen. RequireRole sits outside the Suspense boundary on purpose:
 * a visitor without the role is sent away before the screen's chunk is downloaded, and the
 * server render (which has no session) produces the same spinner as the lazy boundary does.
 */
const guardedElement = (role, Component) => (
  <RequireRole role={role} fallback={<RouteFallback />}>
    {lazyElement(Component)}
  </RequireRole>
);

const staticBreadcrumb =
  (label) =>
  ({ pathname }) => [{ label, to: pathname }];

const resolveAdminBreadcrumb = () => [{ label: "Admin", to: "/admin" }];

const resolveMachineBreadcrumb = ({ params, machines, pathname }) => {
  const machineId = Number.parseInt(params.id, 10);
  const machinePath = Number.isInteger(machineId) ? `/machines/${machineId}` : pathname;
  const machineLabel = Number.isInteger(machineId)
    ? machines.find((machine) => machine.id === machineId)?.title || "Maskin"
    : "Maskin";

  return [
    { label: "Katalog", to: "/catalogue" },
    { label: machineLabel, to: machinePath },
  ];
};

const resolveQuotationBreadcrumb = ({ params, machines, pathname }) => {
  const machineId = Number.parseInt(params.id, 10);
  const quotationPath = Number.isInteger(machineId) ? `/machines/${machineId}/quotation` : pathname;
  const machinePath = Number.isInteger(machineId) ? `/machines/${machineId}` : "/catalogue";

  return [
    ...resolveMachineBreadcrumb({ params, machines, pathname: machinePath }),
    { label: "Begär offert", to: quotationPath },
  ];
};

const resolveCategoryBreadcrumb = ({ params, machines = [], categories = [], pathname }) => {
  // By URL slug, with the database slug still accepted so a legacy link shows the right name
  // during the moment before the redirect lands.
  const match = (category) =>
    categoryUrlSlug(category) === params.slug || category.slug === params.slug;

  const label =
    categories.find(match)?.name ||
    machines.find(
      (machine) =>
        machineCategoryUrlSlug(machine) === params.slug || machine.categorySlug === params.slug
    )?.category ||
    "Kategori";

  return [
    { label: "Katalog", to: "/catalogue" },
    { label, to: pathname },
  ];
};

const resolveManufacturerBreadcrumb = ({ params, machines = [], pathname }) => {
  const label =
    machines.find((machine) => slugify(machine.manufacturer) === params.slug)?.manufacturer ||
    "Fabrikat";

  return [
    { label: "Katalog", to: "/catalogue" },
    { label, to: pathname },
  ];
};

const resolveJobBreadcrumb = ({ params, jobs, pathname }) => {
  const jobLabel = jobs.find((job) => job.slug === params.id)?.title || "Jobbannons";
  const jobPath = params.id ? pathname : "/jobs";

  return [
    { label: "Jobb", to: "/jobs" },
    { label: jobLabel, to: jobPath },
  ];
};

const resolveNewsBreadcrumb = ({ params, news, pathname }) => {
  const articleId = Number.parseInt(params.id, 10);
  const newsLabel = Number.isInteger(articleId)
    ? news.find((article) => article.id === articleId)?.title || "Nyhetsartikel"
    : "Nyhetsartikel";
  const newsPath = Number.isInteger(articleId) ? pathname : "/news";

  return [
    { label: "Nyheter", to: "/news" },
    { label: newsLabel, to: newsPath },
  ];
};

const resolveCreateQuotationBreadcrumb = ({ params, machines, pathname }) => {
  const machineId = Number.parseInt(params.id, 10);
  const createQuotationPath = Number.isInteger(machineId)
    ? `/machines/${machineId}/quotation/create`
    : pathname;
  const quotationPath = Number.isInteger(machineId)
    ? `/machines/${machineId}/quotation`
    : "/catalogue";

  return [
    ...resolveQuotationBreadcrumb({ params, machines, pathname: quotationPath }),
    { label: "Skapa offert", to: createQuotationPath },
  ];
};

const resolveAdminSimpleBreadcrumb =
  (label) =>
  ({ pathname }) => [...resolveAdminBreadcrumb(), { label, to: pathname }];

const resolveAdminCustomersBreadcrumb = ({ pathname }) => [
  ...resolveAdminBreadcrumb(),
  { label: "Kunder", to: pathname },
];

const resolveAdminCreateCustomerBreadcrumb = ({ pathname }) => [
  ...resolveAdminBreadcrumb(),
  { label: "Kunder", to: "/admin/customers" },
  { label: "Skapa kund", to: pathname },
];

const resolveAdminCustomerBreadcrumb = ({ params, pathname }) => [
  ...resolveAdminBreadcrumb(),
  { label: "Kunder", to: "/admin/customers" },
  { label: params.customerId || "Kund", to: pathname },
];

const resolveAdminCustomerSellProposalsBreadcrumb = ({ params, pathname }) => {
  const customerPath = params.customerId
    ? `/admin/customers/${params.customerId}`
    : "/admin/customers";

  return [
    ...resolveAdminBreadcrumb(),
    { label: "Kunder", to: "/admin/customers" },
    { label: params.customerId || "Kund", to: customerPath },
    { label: "Kundens offertförfrågningar", to: pathname },
  ];
};

const publicRoutes = [
  { path: "/", element: <HomePage />, resolveBreadcrumb: staticBreadcrumb("Hitta din maskin") },
  {
    path: "/machines/:id",
    element: <Machine />,
    resolveBreadcrumb: resolveMachineBreadcrumb,
  },
  {
    path: "/machines/:id/quotation",
    element: <Quotation />,
    resolveBreadcrumb: resolveQuotationBreadcrumb,
  },
  { path: "/catalogue", element: <Catalogue />, resolveBreadcrumb: staticBreadcrumb("Katalog") },
  {
    path: "/catalogue/:slug",
    element: <CategoryPage />,
    resolveBreadcrumb: resolveCategoryBreadcrumb,
  },
  {
    path: "/manufacturers/:slug",
    element: <ManufacturerPage />,
    resolveBreadcrumb: resolveManufacturerBreadcrumb,
  },
  { path: "/faq", element: <Faq />, resolveBreadcrumb: staticBreadcrumb("Frågor och svar") },
  { path: "/contact", element: <ContactPage />, resolveBreadcrumb: staticBreadcrumb("Kontakt") },
  {
    path: "/financing",
    element: <Financing />,
    resolveBreadcrumb: staticBreadcrumb("Finansiering"),
  },
  { path: "/about-us", element: <AboutUs />, resolveBreadcrumb: staticBreadcrumb("Om oss") },
  {
    path: "/terms-and-conditions",
    element: <TermsAndConditions />,
    resolveBreadcrumb: staticBreadcrumb("Köpvillkor"),
  },
  { path: "/jobs", element: <JobOffers />, resolveBreadcrumb: staticBreadcrumb("Jobb") },
  {
    path: "/jobs/:id",
    element: <JobDescription />,
    resolveBreadcrumb: resolveJobBreadcrumb,
  },
  {
    // Sits under a public path but is an admin tool: it redirects anyone else and is noindex,
    // so it is lazy and guarded like the rest of them. The server render of this one URL is a spinner.
    path: "/machines/:id/quotation/create",
    element: guardedElement("admin", CreateQuotation),
    resolveBreadcrumb: resolveCreateQuotationBreadcrumb,
  },
  { path: "/news", element: <NewsCatalogue />, resolveBreadcrumb: staticBreadcrumb("Nyheter") },
  {
    path: "/news/:id",
    element: <News />,
    resolveBreadcrumb: resolveNewsBreadcrumb,
  },
  // Last on purpose: react-router ranks a splat below every other pattern, so this only catches
  // a locale-prefixed path no real page answers to. Without it the request falls through to the
  // root catch-all, which redirects to the URL it is already on and leaves the outlet empty.
  { path: "*", element: <NotFound /> },
];

const adminRoutes = [
  {
    path: "/admin/machines/create",
    element: guardedElement("admin", CreateMachine),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Skapa maskin"),
  },
  {
    path: "/admin/machines/unpublished",
    element: guardedElement("admin", UnpublishedMachineManagement),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Avpublicerade maskiner"),
  },
  {
    path: "/admin/sell-proposals",
    element: guardedElement("admin", QuotationManagement),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Offertförfrågningar"),
  },
  {
    path: "/admin/news/create",
    element: guardedElement("admin", CreateNews),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Skapa nyhet"),
  },
  {
    // Each tile on the landing page is filtered by role, so anyone who holds a role gets in, whichever
    // it is. The pages behind the tiles are guarded one by one.
    path: "/admin",
    element: guardedElement(ANY_ROLE, Admin),
    resolveBreadcrumb: staticBreadcrumb("Admin"),
  },
  {
    path: "/admin/customers",
    element: guardedElement("admin", Customers),
    resolveBreadcrumb: resolveAdminCustomersBreadcrumb,
  },
  {
    path: "/admin/customers/create",
    element: guardedElement("admin", CreateCustomer),
    resolveBreadcrumb: resolveAdminCreateCustomerBreadcrumb,
  },
  {
    path: "/admin/customers/:customerId",
    element: guardedElement("admin", CreateCustomer),
    resolveBreadcrumb: resolveAdminCustomerBreadcrumb,
  },
  {
    path: "/admin/customers/:customerId/sell-proposals",
    element: guardedElement("admin", CustomerQuotations),
    resolveBreadcrumb: resolveAdminCustomerSellProposalsBreadcrumb,
  },
  {
    path: "/admin/site-content",
    element: guardedElement("admin", SiteContentAdmin),
    resolveBreadcrumb: staticBreadcrumb("Webbinnehåll"),
  },
  {
    path: "/admin/categories",
    element: guardedElement("admin", CategoriesAdmin),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Kategorier"),
  },
  {
    path: "/admin/jobs",
    element: guardedElement("admin", JobsAdmin),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Jobbannonser"),
  },
  {
    path: "/admin/faq",
    element: guardedElement("admin", FaqAdmin),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Vanliga frågor"),
  },
  {
    path: "/admin/terms",
    element: guardedElement("admin", TermsAdmin),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Köpvillkor"),
  },
  {
    path: "/admin/team",
    element: guardedElement("admin", TeamAdmin),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Team"),
  },
  {
    path: "/admin/users",
    element: guardedElement("user_admin", UsersAdmin),
    resolveBreadcrumb: resolveAdminSimpleBreadcrumb("Användare"),
  },
];

// Combined for backward compatibility (used by breadcrumbs etc.)
const routeComponentMappings = [...publicRoutes, ...adminRoutes];

export { publicRoutes, adminRoutes };
export default routeComponentMappings;
